/**
 * System field definitions — single source of truth.
 *
 * Used by:
 *   • src/pages/AdminCenter.js — renders the merged system + custom field list
 *     in the Custom Fields tab
 *   • src/data/store.js — useFieldOptions hook reads default options from these
 *     definitions when no per-org override is set
 *
 * "Tier" semantics live in AdminCenter (LOCKED_FIELD_IDS, TIER_B_FIELD_IDS).
 * Per-org overrides on Tier B fields' options live in customFields.__overrides
 * (see store.js setSystemFieldOptions).
 */

export const DEFAULT_CONTACT_FIELDS = [
  { id: "fn", label: "First Name", type: "text", required: true, system: true },
  { id: "ln", label: "Last Name", type: "text", required: true, system: true },
  { id: "email", label: "Email", type: "email", required: true, system: true },
  { id: "ph", label: "Phone", type: "phone", required: false, system: true },
  { id: "title", label: "Job Title", type: "text", required: false, system: true },
  { id: "co", label: "Company", type: "text", required: false, system: true },
  { id: "owner", label: "Contact Owner", type: "user", required: true, system: true },
  { id: "source", label: "Lead Source", type: "select", options: ["Inbound", "Outbound", "Referral", "Event", "Website", "LinkedIn"], system: true },
  { id: "type", label: "Account Type", type: "select", options: ["Enterprise", "Mid-Market", "SMB", "Startup"], system: true },
  { id: "industry", label: "Industry", type: "select", options: ["Technology", "Healthcare", "Finance", "Retail", "Aerospace", "Manufacturing", "Other"], system: true },
  { id: "status", label: "Status", type: "select", options: ["active", "nurture", "inactive", "churned"], system: true },
  { id: "score", label: "Lead Score", type: "number", system: true },
  { id: "city", label: "City", type: "text", system: true },
  { id: "state", label: "State/Region", type: "text", system: true },
];

export const DEFAULT_DEAL_FIELDS = [
  { id: "name", label: "Deal Name", type: "text", required: true, system: true },
  { id: "val", label: "Amount", type: "currency", required: true, system: true },
  { id: "stage", label: "Stage", type: "select", system: true },
  { id: "prob", label: "Probability", type: "number", system: true },
  { id: "close", label: "Close Date", type: "date", system: true },
  { id: "co", label: "Company", type: "text", system: true },
  { id: "ct", label: "Primary Contact", type: "user", system: true },
  { id: "risk", label: "Risk Level", type: "select", options: ["none", "low", "medium", "high"], system: true },
];

export const DEFAULT_COMPANY_FIELDS = [
  { id: "name", label: "Company Name", type: "text", required: true, system: true },
  { id: "industry", label: "Industry", type: "select", system: true },
  { id: "revenue", label: "Annual Revenue", type: "currency", system: true },
  { id: "employees", label: "Employees", type: "number", system: true },
  { id: "city", label: "City", type: "text", system: true },
  { id: "state", label: "State", type: "text", system: true },
  { id: "type", label: "Segment", type: "select", options: ["Enterprise", "Mid-Market", "SMB"], system: true },
];

export const DEFAULT_TASK_FIELDS = [
  { id: "title", label: "Title", type: "text", required: true, system: true },
  { id: "due", label: "Due Date", type: "date", system: true },
  { id: "pri", label: "Priority", type: "select", options: ["low", "medium", "high", "urgent"], system: true },
  { id: "assignee", label: "Assigned To", type: "user", system: true },
  { id: "ct", label: "Contact", type: "text", system: true },
  { id: "deal", label: "Deal", type: "text", system: true },
  { id: "done", label: "Completed", type: "checkbox", system: true },
];

export const DEFAULT_CALL_FIELDS = [
  { id: "title", label: "Title", type: "text", system: true },
  { id: "duration", label: "Duration", type: "number", system: true },
  { id: "disposition", label: "Disposition", type: "select", options: ["Connected", "Voicemail", "No Answer", "Busy", "Left Message", "Callback", "Wrong Number"], system: true },
  { id: "notes", label: "Notes", type: "textarea", system: true },
  { id: "contactId", label: "Contact", type: "text", system: true },
];

export const DEFAULT_EMAIL_FIELDS = [
  { id: "subject", label: "Subject", type: "text", system: true },
  { id: "to", label: "To", type: "email", system: true },
  { id: "from", label: "From", type: "email", system: true },
  { id: "status", label: "Status", type: "select", options: ["sent", "delivered", "opened", "clicked", "replied", "bounced"], system: true },
  { id: "contactId", label: "Contact", type: "text", system: true },
];

export const DEFAULT_SMS_FIELDS = [
  { id: "body", label: "Message", type: "textarea", system: true },
  { id: "to", label: "To", type: "phone", system: true },
  { id: "from", label: "From", type: "phone", system: true },
  { id: "direction", label: "Direction", type: "select", options: ["outbound", "inbound"], system: true },
  { id: "status", label: "Status", type: "select", options: ["sent", "delivered", "failed"], system: true },
];

export const DEFAULT_CALENDAR_FIELDS = [
  { id: "title", label: "Title", type: "text", system: true },
  { id: "start", label: "Start", type: "date", system: true },
  { id: "end", label: "End", type: "date", system: true },
  { id: "location", label: "Location", type: "text", system: true },
  { id: "type", label: "Type", type: "select", options: ["meeting", "call", "demo", "follow-up", "internal"], system: true },
  { id: "contactId", label: "Contact", type: "text", system: true },
];

// Map of entity name → field defs, for cross-entity lookups (e.g. useFieldOptions).
export const SYSTEM_FIELDS = {
  contacts: DEFAULT_CONTACT_FIELDS,
  deals: DEFAULT_DEAL_FIELDS,
  companies: DEFAULT_COMPANY_FIELDS,
  tasks: DEFAULT_TASK_FIELDS,
  calls: DEFAULT_CALL_FIELDS,
  emails: DEFAULT_EMAIL_FIELDS,
  sms: DEFAULT_SMS_FIELDS,
  calendar: DEFAULT_CALENDAR_FIELDS,
};
